<template>
    <div class="relative">
        <div class="mb-6 flex items-center justify-between">
            <div class="space-y-1">
                <ULink to="/transports" class="flex items-center space-x-1 text-sm font-semibold text-gray-500 hover:text-gray-700">
                    <span>Transports</span>
                    <UIcon name="i-ph-caret-right-bold" class="size-2.5" />
                </ULink>
                <div v-if="transport" class="flex items-center space-x-4">
                    <h1 class="text-3xl font-semibold text-gray-700">Transport #{{ transport.number.toString().padStart(6, '0') }}</h1>
                    <BadgeTransportStatus :status="transport.status" />
                </div>
            </div>

            <div v-if="transport" class="flex items-center gap-x-2">
                <UDropdown :items="actions">
                    <UButton color="black" label="Actions" icon="i-ph-caret-down-bold" trailing />
                </UDropdown>
            </div>
        </div>

        <div v-if="transport" class="relative flex items-start gap-4">
            <div class="sticky top-0 flex-shrink-0 w-96 space-y-4">
                <div class="relative bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
                    <div class="flex items-center px-6 py-3 border-b border-gray-300">
                        <h3 class="text-lg text-gray-700 font-bold">Details</h3>
                    </div>
                    <div class="px-6 py-5 space-y-4">
                        <Field label="Number">
                            <span class="font-mono">#{{ transport.number.toString().padStart(6, '0') }}</span>
                        </Field>
                        <Field label="Status">
                            <BadgeTransportStatus :status="transport.status" />
                        </Field>
                        <Field label="Provider">
                            <div v-if="transport.provider" class="flex items-center space-x-2.5 mt-1">
                                <Avatar bucket="media" folder="providers" size="sm" :placeholder="transport.provider.name" :path="transport.provider.avatar" />
                                <span>{{ transport.provider.name }}</span>
                            </div>
                        </Field>
                        <Field label="Customer">
                            <ULink v-if="transport.customer" :to="`/customers/${transport.customer_id}`" class="hover:underline">
                                {{ [transport.customer.first_name, transport.customer.middle_name, transport.customer.last_name].filter(Boolean).join(' ') }}
                            </ULink>
                        </Field>
                        <Field label="Trip Type">
                            <span>{{ tripTypeLabels[transport.trip_type] || transport.trip_type }}</span>
                        </Field>
                        <Field label="Scheduled">
                            <span>{{ dayjs(transport.scheduled_date).format('MMM D, YYYY') }} at {{ transport.scheduled_time?.slice(0, 5) }}</span>
                        </Field>
                        <div class="grid grid-cols-2 gap-4">
                            <Field label="Est. Fee">
                                <span v-if="transport.estimated_fee != null" class="font-mono">${{ transport.estimated_fee }}</span>
                                <span v-else class="text-gray-400">–</span>
                            </Field>
                            <Field label="Final Price">
                                <span v-if="transport.price != null" class="font-mono">${{ transport.price }}</span>
                                <span v-else class="text-gray-400">–</span>
                            </Field>
                        </div>
                        <Field v-if="transport.invoice_id" label="Invoice">
                            <ULink :to="`/invoices/providers/${transport.invoice_id}`" class="font-mono hover:underline text-primary-600">
                                View Invoice
                            </ULink>
                        </Field>
                        <Field label="Created By">
                            <span v-if="transport.creator">{{ transport.creator.name }}</span>
                            <span v-else class="text-gray-400">–</span>
                        </Field>
                        <Field label="Created">
                            <span>{{ dayjs(transport.created_at).format('MMM D, YYYY HH:mm') }}</span>
                        </Field>
                    </div>
                </div>
            </div>

            <div class="flex-1 space-y-4">
                <div class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
                    <div class="flex items-center px-6 py-3 border-b border-gray-300">
                        <h3 class="text-lg text-gray-700 font-bold">Patient</h3>
                    </div>
                    <div class="px-6 py-5 grid grid-cols-2 gap-4">
                        <Field label="Name">
                            <span>{{ [transport.customer?.first_name, transport.customer?.middle_name, transport.customer?.last_name].filter(Boolean).join(' ') || '–' }}</span>
                        </Field>
                        <Field label="Phone">
                            <PhoneLabel v-if="transport.customer?.phone" :value="transport.customer.phone" />
                            <span v-else class="text-gray-400">–</span>
                        </Field>
                    </div>
                </div>

                <div v-if="transport.companion_name" class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
                    <div class="flex items-center px-6 py-3 border-b border-gray-300">
                        <h3 class="text-lg text-gray-700 font-bold">Companion</h3>
                    </div>
                    <div class="px-6 py-5 grid grid-cols-2 gap-4">
                        <Field label="Name">
                            <span>{{ transport.companion_name }}</span>
                        </Field>
                        <Field label="Phone">
                            <PhoneLabel v-if="transport.companion_phone" :value="transport.companion_phone" />
                            <span v-else class="text-gray-400">–</span>
                        </Field>
                        <Field v-if="transport.companion_relationship" label="Relationship">
                            <span>{{ transport.companion_relationship }}</span>
                        </Field>
                    </div>
                </div>

                <div class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
                    <div class="flex items-center px-6 py-3 border-b border-gray-300">
                        <h3 class="text-lg text-gray-700 font-bold">Transport Details</h3>
                    </div>
                    <div class="px-6 py-5 grid grid-cols-2 gap-4">
                        <Field label="Reason">
                            <span>{{ reasonLabels[transport.reason] || transport.reason }}</span>
                        </Field>
                        <Field v-if="transport.reason_details" label="Details">
                            <span>{{ transport.reason_details }}</span>
                        </Field>
                        <Field v-if="transport.appointment_time" label="Appointment Time">
                            <span>{{ transport.appointment_time?.slice(0, 5) }}</span>
                        </Field>
                        <Field v-if="transport.posture" label="Posture">
                            <span>{{ postureLabels[transport.posture] || transport.posture }}</span>
                        </Field>
                        <Field v-if="transport.mobility" label="Mobility">
                            <span>{{ mobilityLabels[transport.mobility] || transport.mobility }}</span>
                        </Field>
                    </div>
                </div>

                <div class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
                    <div class="flex items-center px-6 py-3 border-b border-gray-300">
                        <h3 class="text-lg text-gray-700 font-bold">Pickup</h3>
                    </div>
                    <div class="px-6 py-5 grid grid-cols-2 gap-4">
                        <Field label="Type">
                            <span>{{ transport.pickup_type ? (locationTypeLabels[transport.pickup_type] || transport.pickup_type) : '–' }}</span>
                        </Field>
                        <template v-if="transport.pickup_type === 'medical_facility'">
                            <Field label="Facility">
                                <span>{{ transport.departure_address?.name || transport.origin_hospital || '–' }}</span>
                            </Field>
                            <Field v-if="transport.departure_address?.department || transport.origin_department" label="Department">
                                <span>{{ transport.departure_address?.department || transport.origin_department }}</span>
                            </Field>
                            <Field v-if="transport.departure_address?.room || transport.origin_room" label="Room">
                                <span>{{ transport.departure_address?.room || transport.origin_room }}</span>
                            </Field>
                        </template>
                        <template v-else>
                            <Field v-if="transport.departure_address?.city" label="City">
                                <span>{{ transport.departure_address.city }}</span>
                            </Field>
                            <Field v-if="transport.departure_address?.street" label="Street">
                                <span>{{ transport.departure_address.street }}</span>
                            </Field>
                            <Field v-if="transport.departure_address?.building" label="Building">
                                <span>{{ transport.departure_address.building }}</span>
                            </Field>
                            <Field v-if="transport.departure_address?.floor" label="Floor">
                                <span>{{ transport.departure_address.floor }}</span>
                            </Field>
                            <Field v-if="addressFlags(transport.departure_address)" label="Access Notes" class="col-span-2">
                                <span>{{ addressFlags(transport.departure_address) }}</span>
                            </Field>
                        </template>
                        <Field v-if="transport.departure_address?.formatted" label="Full Address" class="col-span-2">
                            <span>{{ transport.departure_address.formatted }}</span>
                        </Field>
                    </div>
                </div>

                <div class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
                    <div class="flex items-center px-6 py-3 border-b border-gray-300">
                        <h3 class="text-lg text-gray-700 font-bold">Dropoff</h3>
                    </div>
                    <div class="px-6 py-5 grid grid-cols-2 gap-4">
                        <Field label="Type">
                            <span>{{ transport.dropoff_type ? (locationTypeLabels[transport.dropoff_type] || transport.dropoff_type) : '–' }}</span>
                        </Field>
                        <template v-if="transport.dropoff_type === 'medical_facility'">
                            <Field v-if="transport.arrival_address?.name" label="Facility">
                                <span>{{ transport.arrival_address.name }}</span>
                            </Field>
                            <Field v-if="transport.arrival_address?.department" label="Department">
                                <span>{{ transport.arrival_address.department }}</span>
                            </Field>
                            <Field v-if="transport.arrival_address?.room" label="Room">
                                <span>{{ transport.arrival_address.room }}</span>
                            </Field>
                        </template>
                        <template v-else>
                            <Field v-if="transport.arrival_address?.city" label="City">
                                <span>{{ transport.arrival_address.city }}</span>
                            </Field>
                            <Field v-if="transport.arrival_address?.street" label="Street">
                                <span>{{ transport.arrival_address.street }}</span>
                            </Field>
                            <Field v-if="transport.arrival_address?.building" label="Building">
                                <span>{{ transport.arrival_address.building }}</span>
                            </Field>
                            <Field v-if="transport.arrival_address?.floor" label="Floor">
                                <span>{{ transport.arrival_address.floor }}</span>
                            </Field>
                            <Field v-if="addressFlags(transport.arrival_address)" label="Access Notes" class="col-span-2">
                                <span>{{ addressFlags(transport.arrival_address) }}</span>
                            </Field>
                        </template>
                        <Field v-if="transport.arrival_address?.formatted" label="Full Address" class="col-span-2">
                            <span>{{ transport.arrival_address.formatted }}</span>
                        </Field>
                    </div>
                </div>

                <div class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
                    <div class="flex items-center px-6 py-3 border-b border-gray-300">
                        <h3 class="text-lg text-gray-700 font-bold">Medical</h3>
                    </div>
                    <div class="px-6 py-5 grid grid-cols-2 gap-4">
                        <Field label="Treating Doctor">
                            <span v-if="transport.treating_doctor">{{ transport.treating_doctor }}</span>
                            <span v-else class="text-gray-400">–</span>
                        </Field>
                        <Field label="Has Medical Report">
                            <span>{{ transport.has_medical_report ? 'Yes' : 'No' }}</span>
                        </Field>
                    </div>
                </div>

                <div v-if="transport.notes" class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
                    <div class="flex items-center px-6 py-3 border-b border-gray-300">
                        <h3 class="text-lg text-gray-700 font-bold">Notes</h3>
                    </div>
                    <div class="px-6 py-5">
                        <p class="text-sm text-gray-700">{{ transport.notes }}</p>
                    </div>
                </div>

                <div v-if="transport.price != null || transport.remarks" class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
                    <div class="flex items-center px-6 py-3 border-b border-gray-300">
                        <h3 class="text-lg text-gray-700 font-bold">Provider Response</h3>
                    </div>
                    <div class="px-6 py-5 grid grid-cols-2 gap-4">
                        <Field v-if="transport.price != null" label="Price">
                            <span class="font-mono">${{ transport.price }}</span>
                        </Field>
                        <Field v-if="transport.remarks" label="Remarks">
                            <span>{{ transport.remarks }}</span>
                        </Field>
                    </div>
                </div>
            </div>
        </div>

        <UModal v-model="showShareModal">
            <UCard>
                <template #header>
                    <div class="flex items-center justify-between">
                        <h3 class="text-lg font-semibold">Share with Provider</h3>
                        <UButton icon="i-ph-x" variant="ghost" color="gray" @click="showShareModal = false" />
                    </div>
                </template>
                <p class="text-sm text-gray-500">
                    Email <strong>{{ transport?.provider?.name }}</strong> with the transport details. The link is valid for 30 days.
                </p>
                <template #footer>
                    <div class="flex gap-3 justify-end">
                        <UButton color="gray" variant="ghost" @click="showShareModal = false">Cancel</UButton>
                        <UButton color="black" :loading="submitting" @click="shareTransport">Send Email</UButton>
                    </div>
                </template>
            </UCard>
        </UModal>

        <TransportResendEmailModal
            v-model="showResendModal"
            :shared-at="transport?.shared_at"
            :submitting="submitting"
            @submit="resendTransportEmail"
        />

        <UModal v-model="showStatusModal">
            <UCard>
                <template #header>
                    <div class="flex items-center justify-between">
                        <h3 class="text-lg font-semibold">
                            Mark as {{ confirmAction === 'accepted' ? 'Accepted' : 'Declined' }}
                        </h3>
                        <UButton icon="i-ph-x" variant="ghost" color="gray" @click="showStatusModal = false" />
                    </div>
                </template>
                <p class="text-sm text-gray-500">Confirm you want to update the status on behalf of the provider.</p>
                <template #footer>
                    <div class="flex gap-3 justify-end">
                        <UButton color="gray" variant="ghost" @click="showStatusModal = false">Cancel</UButton>
                        <UButton
                            :color="confirmAction === 'accepted' ? 'violet' : 'red'"
                            :loading="submitting"
                            @click="confirmAction === 'accepted' ? onAcceptSubmit({ remarks: null }) : onDeclineSubmit({ remarks: null })"
                        >
                            Confirm
                        </UButton>
                    </div>
                </template>
            </UCard>
        </UModal>

        <TransportCompleteModal
            v-model="showCompleteModal"
            :submitting="submitting"
            :initial-price="completionPrice"
            @submit="onCompleteSubmit"
        />

        <TransportCancelModal
            v-model="showCancelModal"
            :submitting="submitting"
            @submit="onCancelSubmit"
        />
    </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import { TransportStatus, TransportReason, TransportPosture, TransportMobility, TransportTripType, LocationType, type Transport, type TransportAddress } from '~/types/transport'
import { UserRole } from '~/types/user'

definePageMeta({ layout: 'default', middleware: 'auth', roles: [UserRole.admin, UserRole.medicalDirector, UserRole.doctor, UserRole.operator] })
useHead({ title: 'View Transport' })

const route = useRoute()
const client = useSupabaseClient()
const toast = useToast()

const { build: buildTransportActions } = useTransportActions()

const transport = ref<Transport>()

const showShareModal = ref(false)
const showResendModal = ref(false)
const showStatusModal = ref(false)
const showCompleteModal = ref(false)
const showCancelModal = ref(false)

const confirmAction = ref<'accepted' | 'declined' | null>(null)
const completionPrice = ref<number | null>(null)
const submitting = ref(false)

const reasonLabels: Record<string, string> = {
    [TransportReason.returnHome]: 'Return Home',
    [TransportReason.assessmentAppointment]: 'Assessment Appointment',
    [TransportReason.medicalVisit]: 'Medical Visit',
    [TransportReason.hospitalAppointment]: 'Hospital Appointment',
}

const postureLabels: Record<string, string> = {
    [TransportPosture.walksAlone]: 'Walks Alone',
    [TransportPosture.needsCane]: 'Needs a Cane',
    [TransportPosture.needsHelp]: 'Needs Help',
}

const mobilityLabels: Record<string, string> = {
    [TransportMobility.wheelchair]: 'Wheelchair',
    [TransportMobility.bedridden]: 'Completely Bedridden',
}

const tripTypeLabels: Record<string, string> = {
    [TransportTripType.oneWay]: 'One Way',
    [TransportTripType.roundTrip]: 'Round Trip',
}

const locationTypeLabels: Record<string, string> = {
    [LocationType.medicalFacility]: 'Medical Facility',
    [LocationType.residential]: 'Residential',
}

function addressFlags(addr: TransportAddress | null | undefined): string | null {
    if (!addr) return null
    const flags = [
        addr.narrow_stairs ? 'Narrow stairs' : null,
        addr.no_electricity ? 'No electricity' : null,
        addr.no_elevator ? 'No elevator' : null,
    ].filter(Boolean) as string[]
    return flags.length ? flags.join(' · ') : null
}

const isEditable = computed(() => {
    return transport.value && ![TransportStatus.completed, TransportStatus.cancelled].includes(transport.value.status)
})

const actions = computed(() => {
    if (!transport.value) return []
    return buildTransportActions(transport.value, {
        onEdit: t => navigateTo(`/transports/create?id=${t.id}`),
        onViewInvoice: t => t.invoice_id && navigateTo(`/invoices/providers/${t.invoice_id}`),
        onShare: () => { showShareModal.value = true },
        onResendEmail: () => { showResendModal.value = true },
        onCopyLink: t => copyShareLink(t),
        onAccept: () => { confirmAction.value = 'accepted'; showStatusModal.value = true },
        onDecline: () => { confirmAction.value = 'declined'; showStatusModal.value = true },
        onComplete: t => { completionPrice.value = t.estimated_fee ?? null; showCompleteModal.value = true },
        onCancel: () => { showCancelModal.value = true },
    })
})

async function copyShareLink(t: Transport) {
    try {
        const { url } = await $fetch<{ url: string; expires_at: string }>(`/api/transports/${t.id}/share-link`)
        await navigator.clipboard.writeText(url)
        toast.add({ title: 'Link copied', description: 'Valid for 30 days.', color: 'green' })
    } catch (e: any) {
        toast.add({ title: e?.data?.message || 'Failed to copy link', color: 'red' })
    }
}

async function shareTransport() {
    if (!transport.value) return
    submitting.value = true
    try {
        await $fetch('/api/transports/share', { method: 'POST', body: { id: transport.value.id } })
        showShareModal.value = false
        toast.add({ title: 'Email sent', color: 'green' })
        await fetchData()
    } catch (e: any) {
        toast.add({ title: e?.data?.message || 'Failed to share', color: 'red' })
    } finally {
        submitting.value = false
    }
}

async function resendTransportEmail() {
    if (!transport.value) return
    submitting.value = true
    try {
        await $fetch('/api/transports/share', { method: 'POST', body: { id: transport.value.id } })
        showResendModal.value = false
        toast.add({ title: 'Email resent', color: 'green' })
    } catch (e: any) {
        toast.add({ title: e?.data?.message || 'Failed to resend email', color: 'red' })
    } finally {
        submitting.value = false
    }
}

async function transition(action: 'accept' | 'decline' | 'complete' | 'cancel', payload: any = {}) {
    if (!transport.value) return
    submitting.value = true
    try {
        await $fetch(`/api/transports/${transport.value.id}/transition`, {
            method: 'POST',
            body: { action, ...payload },
        })
        showStatusModal.value = false
        showCompleteModal.value = false
        showCancelModal.value = false
        const colorMap = { accept: 'violet', complete: 'emerald', decline: 'red', cancel: 'red' } as const
        toast.add({ title: `Transport ${action}ed`, color: colorMap[action] })
        await fetchData()
    } catch (e: any) {
        toast.add({ title: e?.data?.message || 'Failed', color: 'red' })
    } finally {
        submitting.value = false
    }
}

function onAcceptSubmit(_payload: { remarks: string | null }) {
    transition('accept', _payload)
}
function onDeclineSubmit(_payload: { remarks: string | null }) {
    transition('decline', _payload)
}
function onCompleteSubmit(payload: { price: number; remarks: string | null }) {
    transition('complete', payload)
}
function onCancelSubmit(payload: { remarks: string | null }) {
    transition('cancel', payload)
}

function formatAddress(addr: TransportAddress | null | undefined): string {
    if (!addr) return '–'
    const parts = [addr.name, addr.city, addr.street, addr.building].filter(Boolean)
    const extras = [
        addr.floor ? `Floor ${addr.floor}` : null,
        addr.department ? `Dept: ${addr.department}` : null,
    ].filter(Boolean)
    return [...parts, ...extras].join(', ') || '–'
}

onMounted(() => fetchData())

async function fetchData() {
    const { data } = await client
        .from('transports')
        .select(`
            *,
            customer:customers (id, first_name, middle_name, last_name, phone),
            provider:providers (id, name, avatar),
            creator:users (id, name)
        `)
        .eq('id', route.params.id)
        .single()

    if (data) {
        transport.value = data as any
    }
}
</script>
